import React from 'react';
import UserAvatar from '@/components/UserAvatar';
import { capitalizeFirstLetter } from './utils';

/**
 * Detects URLs in text and converts them to clickable links
 * Supports http://, https://, www., and common TLDs
 * Also preserves line breaks by converting \n to <br />
 * Also parses @username mentions if a list of users is provided
 */
export function linkify(text: string, users: any[] = []): React.ReactNode[] {
  // Enhanced URL regex that handles:
  // - http:// and https:// URLs
  // - www. URLs
  // - URLs without protocol but with common TLDs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s]|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:com|org|net|edu|gov|mil|int|info|biz|name|museum|coop|aero|asia|cat|jobs|mobi|tel|travel|xxx|pl|de|uk|fr|jp|cn|ru|br|au|in|it|nl|es|se|no|dk|fi|be|ch|at|cz|pt|gr|ie|hu|ro|bg|hr|sk|si|lt|lv|ee|cy|mt|lu)(?:[/?#][^\s<]*)?)/gi;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const index = match.index;

    // Add text before the URL (with line breaks converted)
    if (index > lastIndex) {
      const textBeforeUrl = text.substring(lastIndex, index);
      parts.push(...convertNewlinesToBreaks(textBeforeUrl, lastIndex));
    }

    // Determine full URL with protocol
    let href = url;
    if (!url.match(/^https?:\/\//i)) {
      href = 'https://' + url;
    }

    // Add the clickable link
    parts.push(
      <a
        key={`link-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-white hover:text-gray-200 underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );

    lastIndex = urlRegex.lastIndex;
  }

  // Add remaining text after the last URL (with line breaks converted)
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    parts.push(...convertNewlinesToBreaks(remainingText, lastIndex));
  }

  // Mentions pass: if users are provided, parse plain strings for @mentions
  if (users && users.length > 0) {
    const result: React.ReactNode[] = [];
    const mentionRegex = /@([a-zA-Z0-9_żółćęśąźńŻÓŁĆĘŚĄŹŃ-]+)/g;

    parts.forEach((part, partIndex) => {
      if (typeof part === 'string') {
        let lastPartIndex = 0;
        let mentionMatch;
        mentionRegex.lastIndex = 0;

        while ((mentionMatch = mentionRegex.exec(part)) !== null) {
          const fullMention = mentionMatch[0];
          const username = mentionMatch[1];
          const index = mentionMatch.index;

          // Add text before the mention
          if (index > lastPartIndex) {
            result.push(part.substring(lastPartIndex, index));
          }

          // Check if there is a matching user
          const matchedUser = users.find(
            u => u.username.toLowerCase() === username.toLowerCase()
          );

          if (matchedUser) {
            result.push(
              <span
                key={`mention-${partIndex}-${index}`}
                className="inline-flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-full border border-indigo-100/50 dark:border-indigo-900/50 font-bold text-xs align-baseline mx-0.5 select-none"
              >
                <UserAvatar
                  name={matchedUser.username}
                  imageUrl={matchedUser.avatarUrl}
                  size="xs"
                  className="h-3.5 w-3.5"
                />
                <span>{capitalizeFirstLetter(matchedUser.username)}</span>
              </span>
            );
          } else {
            result.push(fullMention);
          }

          lastPartIndex = mentionRegex.lastIndex;
        }

        if (lastPartIndex < part.length) {
          result.push(part.substring(lastPartIndex));
        }
      } else {
        result.push(part);
      }
    });

    return result.length > 0 ? result : [text];
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Helper function to convert newlines to <br /> elements
 */
function convertNewlinesToBreaks(text: string, keyOffset: number = 0): React.ReactNode[] {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];

  lines.forEach((line, index) => {
    if (index > 0) {
      result.push(<br key={`br-${keyOffset}-${index}`} />);
    }
    if (line) {
      result.push(line);
    }
  });

  return result;
}

/**
 * Component wrapper for linkified text
 */
export function LinkifiedText({ text, className }: { text: string; className?: string }) {
  return (
    <span className={className}>
      {linkify(text)}
    </span>
  );
}
